import React, { useEffect, useState } from 'react';
import SchoolLayout from '../../components/SchoolLayout';
import axios from 'axios';
import { Users, UserCheck, Building2, Plus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, accent, sub }) => (
  <div className="bg-white rounded border border-[#E5E5E5] p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent + '15', color: accent }}>
        <Icon size={18} />
      </div>
      <div className="text-right">
        <div className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>{value}</div>
      </div>
    </div>
    <div className="text-xs font-bold text-[#555] uppercase tracking-wide">{title}</div>
    {sub && <div className="text-xs text-[#999] mt-0.5">{sub}</div>}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, schoolRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/schools/${user.school_id}`)
      ]);
      setStats(statsRes.data);
      setSchool(schoolRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SchoolLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#999] animate-pulse">Memuatkan data...</div>
        </div>
      </SchoolLayout>
    );
  }

  const canRegisterMembers = school?.status === 'Active';

  return (
    <SchoolLayout>
      <div className="space-y-5 max-w-5xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest mb-1">Portal Perguruan</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Dashboard Perguruan</h1>
            <p className="text-sm text-[#777] mt-0.5">{school?.name}</p>
          </div>
          {canRegisterMembers && (
            <button
              onClick={() => navigate('/school/members/new')}
              className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors self-start sm:self-auto"
              data-testid="add-member-button"
            >
              <Plus size={14} /> Daftar Ahli Baru
            </button>
          )}
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {/* Status Alert */}
        {school?.status !== 'Active' && (
          <div className={`p-4 rounded border-l-4 ${
            school?.status === 'Pending'
              ? 'bg-[#FFFBF0] border-[#D4AF37] text-[#7a6200]'
              : 'bg-[#FEF2F2] border-[#D71920] text-[#D71920]'
          }`} data-testid="status-alert">
            <p className="text-sm font-medium">
              {school?.status === 'Pending'
                ? 'Permohonan perguruan anda sedang menunggu kelulusan RSS. Anda boleh mendaftar ahli selepas diluluskan.'
                : `Status perguruan: ${school?.status}. Sila hubungi pentadbir RSS untuk maklumat lanjut.`
              }
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <StatCard title="Jumlah Ahli" value={stats?.total_members ?? 0} icon={Users} accent="#D71920" />
          <StatCard title="Ahli Aktif" value={stats?.active_members ?? 0} icon={UserCheck} accent="#16a34a" />
          <StatCard
            title="Status Perguruan"
            value={school?.status || '—'}
            icon={Building2}
            accent={school?.status === 'Active' ? '#16a34a' : '#D4AF37'}
          />
        </div>

        {/* Quick Access */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Maklumat Perguruan</div>
            <div className="space-y-0">
              {[
                { label: 'Ketua Jurulatih', value: school?.head_coach_name },
                { label: 'Bahagian / Daerah', value: `${school?.division}, ${school?.district}` },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-[#F7F7F7] last:border-0">
                  <span className="text-xs text-[#777]">{item.label}</span>
                  <span className="text-sm font-semibold text-[#0B0B0B] text-right max-w-[150px] truncate" title={item.value}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Akses Pantas</div>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/school/members')}
                className="w-full flex items-center justify-between p-4 rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-all text-left"
                data-testid="quick-link-members"
              >
                <div>
                  <p className="text-sm font-semibold text-[#0B0B0B]">Urus Ahli</p>
                  <p className="text-xs text-[#777] mt-0.5">Lihat dan kemaskini senarai ahli</p>
                </div>
                <ArrowRight size={14} className="text-[#D71920] flex-shrink-0" />
              </button>
              {user?.role === 'SCHOOL_ADMIN' && (
                <button
                  onClick={() => navigate('/school/profile')}
                  className="w-full flex items-center justify-between p-4 rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-all text-left"
                  data-testid="quick-link-profile"
                >
                  <div>
                    <p className="text-sm font-semibold text-[#0B0B0B]">Profil Perguruan</p>
                    <p className="text-xs text-[#777] mt-0.5">Kemaskini maklumat perguruan</p>
                  </div>
                  <ArrowRight size={14} className="text-[#D71920] flex-shrink-0" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SchoolLayout>
  );
};

export default Dashboard;
